import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  TokenMatchQuery,
  TokenVocabularyQuery,
  type TokenRow,
  type TokenVocabularyRow,
} from '@graphql/queries/PerceptionModelQueries';

import {
  buildTokenVocabulary,
  matchedProducts,
  type TokenOption,
} from './tokenSearch';

/**
 * Token search over the model's own vocabulary — "Highlight by taste".
 *
 * Kept out of PerceptionDataExplorer because it is a self-contained concern
 * with two queries, its own error handling and its own disabled state, and the
 * explorer already owns the map and the selected product.
 *
 * Both reads go through the `perception_model_token*` views, which ship in a
 * separate migration. An environment without them should still get a working
 * map, so the failure is reported locally as "search is unavailable" rather
 * than raised as a page error.
 */

export type TokenSearch = {
  vocabulary: TokenOption[];
  tokens: TokenOption[];
  setTokens: (next: TokenOption[]) => void;
  /** products carrying every chosen token; empty while nothing is chosen */
  matched: Set<string>;
  loading: boolean;
  /** the token views are not present in this environment */
  unavailable: boolean;
};

const EMPTY_MATCHES: Set<string> = new Set();

const useTokenSearch = (
  jobId: number | undefined,
  enabled: boolean,
): TokenSearch => {
  const [tokens, setTokens] = React.useState<TokenOption[]>([]);

  // The vocabulary: 627 rows, grouped by the view rather than in the browser.
  const {
    data: vocabData,
    loading: vocabLoading,
    error: vocabError,
  } = useQuery(TokenVocabularyQuery, {
    variables: { jobId },
    skip: !jobId || !enabled,
    context: { errorHandledLocally: true },
  });

  const vocabulary = React.useMemo(
    () =>
      buildTokenVocabulary(
        (vocabData?.allPerceptionModelTokenVocabularies?.nodes ??
          []) as TokenVocabularyRow[],
      ),
    [vocabData],
  );

  // Only the products carrying the chosen tokens, and only once some are
  // chosen. Scales with how common those tokens are, not with the universe.
  const { data: matchData, loading: matchLoading } = useQuery(TokenMatchQuery, {
    variables: { jobId, tokens: tokens.map((t) => t.token) },
    skip: !jobId || tokens.length === 0,
    context: { errorHandledLocally: true },
  });

  const matched = React.useMemo(
    () =>
      tokens.length === 0
        ? EMPTY_MATCHES
        : matchedProducts(
            (matchData?.allPerceptionModelTokens?.nodes ?? []) as TokenRow[],
            tokens,
          ),
    [matchData, tokens],
  );

  return {
    vocabulary,
    tokens,
    setTokens,
    matched,
    loading: vocabLoading || matchLoading,
    unavailable: !!vocabError,
  };
};

export default useTokenSearch;
